/**
 * The board-info popover behind the title's `⌄` (spec M1-B1 §7.4, P-041).
 *
 * Rebuilt in round 9n from three captures rather than from the old summary:
 * `[ui:library/board-02-description.png]` (production, 4 owners, a description),
 * `[c:board-info-rows]` (sandbox, 1 owner) and `[c:prod-board-info-permissions]` (production,
 * a board whose permission set is not *Edit everything*). Measured at 1440 × 900:
 *
 * | element | geometry |
 * |---|---|
 * | popover | **422** wide, radius 8, `--box-shadow-medium`, 24 px side padding (content 413 → 787) |
 * | title | `h3` 18/24 **500** at y 166; the ☆ is a 32 × 32 button flush right (755 → 787) |
 * | description | 14/21, one line at y 200, the placeholder greyed |
 * | divider | full content width, 8 px above / 16 px below |
 * | **Board info** | 16/22 **600** |
 * | rows | pitch **48**; label 14/20 `--secondary-text-color` in a 120-wide column at x 421; the value is a **238 × 32** button at x 541 |
 *
 * The five rows, in monday's order: **Board type** (kind glyph + word + a chevron that opens
 * *Change to …*), **Permissions** (a padlock + `Limited`, only while the board is not at *Edit
 * everything* — the sandbox board is, and renders four rows, while `MK-CC00` renders five),
 * **Owner/Owners** (an avatar stack; the label is singular for one owner), **Created by** (the
 * creator's avatar and **only** the date — monday prints no name there) and **Notifications**.
 *
 * Dates go through `formatMondayDate` (P-041's locale leak: this row read `8 lug 2025` on an
 * Italian browser because the old call passed no locale).
 */
import { Avatar, EntityIcon, Icon, boardEntityKind, formatMondayDate, icons } from '@elizmonday/ui';
import { useEffect, useRef, useState } from 'react';
import { initialsOf } from '../board-view/columns/primitives';
import { useFavorite } from './favorites';
import { boardMuteLabel } from './BoardNotificationsDialog';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import {
  BOARD_KIND_LABEL,
  useBoard,
  useBoardMembers,
  useBoardMutations,
  useBoardRow,
  type BoardKind,
} from './hooks';

/** What the popover can ask the header to open. */
export type BoardInfoTarget = 'subscribers' | 'permissions' | 'notifications';

/**
 * monday's board `permissions` values against the word the popover prints. `everyone` is the
 * default and renders **no row at all** (`[c:board-info-rows]` — four rows on the sandbox
 * board, which sits at *Edit everything*); everything else is `Limited`
 * (`[c:prod-board-info-permissions]`), never the set's own name.
 */
export function boardPermissionsWord(permissions: string | null | undefined): string | null {
  const value = (permissions ?? 'everyone').trim();
  return value === '' || value === 'everyone' ? null : 'Limited';
}

export function BoardInfoPopover({
  boardId,
  onClose,
  onOpen,
}: {
  boardId: string;
  onClose(): void;
  onOpen(target: BoardInfoTarget): void;
}) {
  const trpc = useTRPC();
  const board = useBoard(boardId);
  const members = useBoardMembers(boardId);
  const mutations = useBoardMutations();
  const favorite = useFavorite('board', boardId);
  const ref = useRef<HTMLDivElement>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [kindMenu, setKindMenu] = useState(false);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (kindMenu) setKindMenu(false);
      else onClose();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose, kindMenu]);

  const kind = (board.data?.kind ?? 'public') as BoardKind;
  const owners = (members.data ?? []).filter((member) => member.role === 'owner');
  const description = board.data?.description ?? '';

  const row = useBoardRow(boardId);
  const mutes = useQuery({ ...trpc.notifications.boardMutes.queryOptions(), staleTime: 60_000 });
  const boardRow = row.data as
    | { createdBy?: number | string | null; createdAt?: string | Date | null; permissions?: string | null }
    | undefined;
  const createdById = boardRow?.createdBy ?? null;
  const createdAt = boardRow?.createdAt ?? null;
  const creator = (members.data ?? []).find((member) => String(member.id) === String(createdById));
  const createdOn = createdAt === null ? '' : formatMondayDate(createdAt);
  const muteState = (mutes.data ?? []).find((entry) => String(entry.boardId) === String(boardId))?.muteState;
  const permissionsWord = boardPermissionsWord(boardRow?.permissions);

  /** The two kinds the board is *not*, as monday words them (`[c:board-type-menu]`). */
  const otherKinds = (['public', 'share', 'private'] as BoardKind[]).filter((entry) => entry !== kind);

  return (
    <div ref={ref} className="ez-board-info" role="dialog" aria-label="Board information" data-testid="board-info">
      <div className="ez-board-info__head">
        <input
          className="ez-board-info__name"
          aria-label="Board name"
          defaultValue={board.data?.name ?? ''}
          data-testid="board-info-name"
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next && next !== board.data?.name) {
              mutations.update.mutate({ id: boardId, attribute: 'name', value: next });
            }
          }}
        />
        <button
          type="button"
          className="ez-board-info__star"
          aria-pressed={favorite.isFavorite}
          aria-label={favorite.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          data-testid="board-info-favorite"
          onClick={() => favorite.toggle()}
        >
          <Icon icon={favorite.isFavorite ? icons.favoriteFilled : icons.favorite} size={16} ignoreFocusStyle />
        </button>
      </div>

      {editingDescription ? (
        <textarea
          className="ez-board-info__description-input"
          aria-label="Board description"
          defaultValue={description}
          maxLength={4000}
          data-testid="board-info-description-input"
          autoFocus
          onBlur={(event) => {
            setEditingDescription(false);
            if (event.target.value !== description) {
              mutations.update.mutate({
                id: boardId,
                attribute: 'description',
                value: event.target.value,
              });
            }
          }}
        />
      ) : (
        <button
          type="button"
          className={`ez-board-info__description${description === '' ? ' ez-board-info__description--empty' : ''}`}
          data-testid="board-info-description"
          onClick={() => setEditingDescription(true)}
        >
          {description === '' ? 'Add a board description' : description}
        </button>
      )}

      <hr className="ez-board-info__divider" />

      <h3 className="ez-board-info__section">Board info</h3>
      <dl className="ez-board-info__grid">
        <div className="ez-board-info__row">
          <dt>Board type</dt>
          <dd>
            <button
              type="button"
              className="ez-board-info__value"
              data-testid="board-info-kind"
              aria-haspopup="menu"
              aria-expanded={kindMenu}
              onClick={() => setKindMenu((open) => !open)}
            >
              <EntityIcon kind={boardEntityKind(kind)} size={16} />
              <span className="ez-board-info__value-text">{BOARD_KIND_LABEL[kind]}</span>
              <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle className="ez-board-info__value-chevron" />
            </button>
            {kindMenu ? (
              <div className="ez-board-info__kind-menu" role="menu" data-testid="board-info-kind-menu">
                {otherKinds.map((next) => (
                  <button
                    key={next}
                    type="button"
                    role="menuitem"
                    className="ez-board-info__kind-item"
                    data-testid={`board-info-kind-${next}`}
                    onClick={() => {
                      setKindMenu(false);
                      mutations.changeKind.mutate({ id: boardId, kind: next });
                    }}
                  >
                    {`Change to ${BOARD_KIND_LABEL[next]}`}
                  </button>
                ))}
              </div>
            ) : null}
          </dd>
        </div>

        {permissionsWord === null ? null : (
          <div className="ez-board-info__row">
            <dt>Permissions</dt>
            <dd>
              <button
                type="button"
                className="ez-board-info__value"
                data-testid="board-info-permissions"
                onClick={() => onOpen('permissions')}
              >
                <Icon icon={icons.locked} size={16} ignoreFocusStyle />
                <span className="ez-board-info__value-text">{permissionsWord}</span>
              </button>
            </dd>
          </div>
        )}

        <div className="ez-board-info__row">
          <dt>{owners.length === 1 ? 'Owner' : 'Owners'}</dt>
          <dd>
            <button
              type="button"
              className="ez-board-info__value ez-board-info__value--people"
              data-testid="board-info-owners"
              onClick={() => onOpen('subscribers')}
            >
              {owners.length === 0 ? (
                <span className="ez-board-info__value-text">No owner</span>
              ) : (
                <>
                  <span className="ez-board-info__avatars">
                    {owners.slice(0, 4).map((owner) => (
                      <Avatar
                        key={owner.id}
                        text={initialsOf(owner.name)}
                        type="text"
                        size="small"
                        aria-label={owner.name}
                      />
                    ))}
                  </span>
                  {/* monday prints the name only while there is exactly one owner; more than
                      one collapses to the stack (`[ui:board-02-description]` vs `[c:board-info-rows]`). */}
                  {owners.length === 1 ? (
                    <span className="ez-board-info__value-text">{owners[0].name}</span>
                  ) : null}
                </>
              )}
            </button>
          </dd>
        </div>

        <div className="ez-board-info__row">
          <dt>Created by</dt>
          <dd>
            <span className="ez-board-info__value" data-testid="board-info-created-by">
              <span className="ez-board-info__avatars">
                <Avatar
                  text={initialsOf(creator?.name ?? '?')}
                  type="text"
                  size="small"
                  aria-label={creator?.name ?? 'Unknown'}
                />
              </span>
              <span className="ez-board-info__value-text">{createdOn === '' ? '' : `on ${createdOn}`}</span>
            </span>
          </dd>
        </div>

        <div className="ez-board-info__row">
          <dt>Notifications</dt>
          <dd>
            <button
              type="button"
              className="ez-board-info__value"
              data-testid="board-info-notifications"
              onClick={() => onOpen('notifications')}
            >
              <Icon icon={icons.bell} size={16} ignoreFocusStyle />
              <span className="ez-board-info__value-text">{boardMuteLabel(muteState)}</span>
            </button>
          </dd>
        </div>
      </dl>
    </div>
  );
}
